import React from 'react';
import PropTypes from 'prop-types';

import ArticleStandardListingItem from '$partials/listings/items/article-standard';
import Carousel, { CarouselSlide } from '$partials/ui/carousel';
import { CarouselProvider } from '$contexts/carousel';
import Button from '$partials/ui/button.jsx';
import { useLocale } from '$contexts/locale';
import { IconChevronSprite } from '$partials/icons/index.js';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @typedef {PropTypes.InferProps<typeof propTypes>} Props
 */
const ModuleRelatedCarousel = ({
    ctaLabel,
    ctaLink,
    items,
    title,
    parent,
}) => {
    const { formatURI, translate: t } = useLocale();
    return (
        <div className={`overflow-hidden bg-white relative ${parent === 'Listing' ? 'pb-40 md:pb-60 pt-70' : parent === 'Detail' ? 'py-20 md:py-30' : 'py-40 md:py-60'}`}>
            <div className='layout-container'>

                <section>
                    <div className='flex flex-wrap items-center justify-between px-10 sm:px-5 md:px-10 gap-y-5'>
                        <h2 className='w-full sm:w-8/12 md:w-7/12 type-style-3 inline mr-10'>
                            {t(title)}
                        </h2>
                        {ctaLabel && ctaLink && (
                            <Button
                                as='a'
                                className='button button--secondary-alt'
                                href={formatURI(ctaLink)}
                                type='button'>
                                <span>{ctaLabel}</span>
                                <span className='button__icon button__icon--animate'>
                                    <IconChevronSprite />
                                </span>
                            </Button>
                        )}
                    </div>
                    <div className={`hidden ${items.length > 2 ? '' : 'sm:flex'} ${items.length > 3 ? 'md:hidden' : 'md:flex'} mt-40`}>
                        {items.map((item, i) => item ? (
                            <article
                                aria-label={item.title}
                                aria-posinset={i + 1}
                                className={'flex w-full sm:w-[246px] md:w-4/12 px-10 sm:px-5 md:px-10'}
                                key={i}
                                tabIndex={-1}>
                                <ArticleStandardListingItem
                                    {...item}
                                    tags={item.categories}
                                    url={`/${item?.__typename === 'BvaBusinessResourcePage' ? 'resources' : 'case-studies'}/${item.slug}`} />
                            </article>
                        ) : null)}
                    </div>
                    <div className={`overflow-hidden w-screen max-w-screen left-1/2 relative -translate-x-1/2 mt-40 ${items.length > 2 ? '' : 'sm:hidden'} ${items.length > 3 ? 'md:block' : 'md:hidden'}`}>
                        <CarouselProvider>
                            <Carousel className='flex swiper-wrapper h-auto w-full image-carousel'>
                                {items.map((item, i) => {
                                    if (!item) { return null; }
                                    return (
                                        <CarouselSlide
                                            className='h-auto flex flex-wrap image-carousel-slide px-10 sm:px-5 md:px-10'
                                            index={i}
                                            key={`${title}-related-article-${i}`}>
                                            <article
                                                aria-label={item.title}
                                                aria-posinset={i + 1}
                                                className='w-full'
                                                tabIndex={-1}>
                                                <ArticleStandardListingItem
                                                    {...item}
                                                    tags={item.categories}
                                                    type='RelatedCarousel'
                                                    url={`/${item.__typename === 'BvaBusinessResourcePage' ? 'resources' : 'case-studies'}/${item.slug}`} />
                                            </article>
                                        </CarouselSlide>
                                    );
                                })}
                            </Carousel>
                        </CarouselProvider>
                    </div>
                </section>
            </div>
        </div>
    );
};

/* ========================================================================= */
/* Typing and export(s) */
/* ========================================================================= */

ModuleRelatedCarousel.propTypes = {
    ctaLabel: PropTypes.string,
    ctaLink: PropTypes.string,
    items: PropTypes.array,
    title: PropTypes.string,
    parent: PropTypes.string,
};

export default ModuleRelatedCarousel;
